// События карточек карты /tutors (02.11.2026): показы и клики в блоке
// «Рекомендуемые» и в общем списке — для CTR блока против CTR списка.
//
// Пишет сервер (/api/map/events → map_partner_events), браузер шлёт пачками
// (src/lib/map-partner-events-client.ts). Здесь — белые списки и проверка
// пачки. Без серверных импортов: модуль читают маршрут, клиент и
// scripts/verify-map-featured.mts.

export const PARTNER_EVENTS = ["featured_impression", "card_impression", "whatsapp_click", "call_click", "details_click"] as const;
export type PartnerEvent = (typeof PARTNER_EVENTS)[number];

export const EVENT_PLACEMENTS = ["featured", "list", "map"] as const;
export type EventPlacement = (typeof EVENT_PLACEMENTS)[number];

/** Не больше событий за вызов. */
export const MAX_EVENTS_PER_CALL = 50;
/** Мягкий потолок: событий с одного IP за минуту. */
export const MAX_EVENTS_PER_IP_MINUTE = 120;
/** Длина пути страницы. */
export const MAX_PAGE_LEN = 200;

export type EventRow = { partner_id: string; event: PartnerEvent; placement: EventPlacement | null; page: string | null };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Только путь: без query и якоря, начинается с «/», не длиннее MAX_PAGE_LEN. Иначе null. */
export function cleanPage(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const path = raw.split(/[?#]/)[0];
  return path.startsWith("/") && path.length <= MAX_PAGE_LEN ? path : null;
}

/**
 * Пачка из тела запроса → строки для вставки. Больше MAX_EVENTS_PER_CALL —
 * null (вызов отклоняется целиком). Событие не из белого списка или id не
 * uuid — пропускается, остальные пишутся.
 */
export function parseEvents(body: unknown): EventRow[] | null {
  const list = Array.isArray(body) ? body : (body as { events?: unknown } | null)?.events;
  if (!Array.isArray(list) || list.length > MAX_EVENTS_PER_CALL) return null;
  const out: EventRow[] = [];
  for (const x of list) {
    const e = x as Record<string, unknown> | null;
    if (!e || typeof e !== "object") continue;
    if (typeof e.partner_id !== "string" || !UUID_RE.test(e.partner_id)) continue;
    if (!(PARTNER_EVENTS as readonly unknown[]).includes(e.event)) continue;
    const placement = e.placement == null ? null : (EVENT_PLACEMENTS as readonly unknown[]).includes(e.placement) ? (e.placement as EventPlacement) : undefined;
    if (placement === undefined) continue;
    out.push({ partner_id: e.partner_id.toLowerCase(), event: e.event as PartnerEvent, placement, page: cleanPage(e.page) });
  }
  return out;
}

/** Пишем только на боевом домене: превью (*.workers.dev) и localhost работают с той же базой. */
export const isEventsHost = (host: string) => host === "yolutap.com";

/**
 * Счётчик событий по IP за минуту — МЯГКАЯ защита: живёт в памяти одного
 * изолята Worker (у каждого изолята свой, после перезапуска пуст). IP только
 * здесь, в базу не пишется. Возвращает, сколько событий из n ещё можно принять.
 */
export function createRateLimiter(limit = MAX_EVENTS_PER_IP_MINUTE, windowMs = 60_000) {
  const hits = new Map<string, { start: number; n: number }>();
  return (ip: string, n: number, now = Date.now()): number => {
    if (hits.size > 5000) for (const [k, v] of hits) if (now - v.start >= windowMs) hits.delete(k);
    let h = hits.get(ip);
    if (!h || now - h.start >= windowMs) {
      h = { start: now, n: 0 };
      hits.set(ip, h);
    }
    const allowed = Math.max(0, Math.min(n, limit - h.n));
    h.n += allowed;
    return allowed;
  };
}
